import { lazy } from 'react'
import PropTypes from 'prop-types'
import { format } from 'date-fns/format'

import PreLineTypography from 'components/UI/PreLineTypography'
import DischargeInstructionsList from 'components/common/DischargeInstructionsList'
import ReferralsList from 'components/common/ReferralsList'

import {
  RestrictionsList,
  PrescriptionsSection,
  DiseasesList,
} from 'components/ModalsContainer/v1Modals/PatientSummary/components'
import { formatNoteDescriptionFromBackend } from 'util/notes'
import { formatDate } from 'util/formatters'
import VisitTypes from 'constants/visit-types'
import MemberExtendedData from 'singletons/MemberExtendedData'
import User from 'singletons/User'

const ClientCard = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/ClientCard'))
const Patient = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Patient'))
const Medical = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Medical'))
const Visits = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Visits'))
const Photos = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Photos'))
const Diagnosis = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Diagnosis'))
const Prescriptions = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Prescriptions'))
const Discharge = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Discharge'))
const Notes = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Notes'))
const Intake = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/Intake'))
const SafetyPlan = lazy(() => import('components/Dashboard/Main/Visit/TabPanel/tabs/SafetyPlan'))

/* For each type of visit we have 5 default tabs: "Client card", "Patient", "Medical", "Visits", "Photos".
Then depends on visit type we have additional tabs, which are only enabled when visit is active.
Medical: "Diagnosis", "Prescriptions", "Discharge".
Therapy: "Intake", "Notes", "Discharge", "Safety Plan" (based on FF).
Psychiatry: "Intake", "Notes"?, "Prescriptions", "Discharge", "Safety Plan" (based on FF).
Health Coaching: "Notes", "Discharge". */

const FormattedNote = ({ note }) => <PreLineTypography>{formatNoteDescriptionFromBackend(note)}</PreLineTypography>

FormattedNote.propTypes = {
  note: PropTypes.string,
}
FormattedNote.defaultProps = {
  note: '',
}

const getFinalContent = ({ additionalTabs = [], additionalSections = [], visitType = null } = {}) => {
  const isSuccessVisit = visitType === VisitTypes.success
  const isCrisisVisit = visitType === VisitTypes.crisis

  return {
    tabs: [
      {
        label: 'Client card',
        key: 'clientCard',
        component: <ClientCard />,
        isActiveVisitDependent: false,
      },
      {
        label: !isSuccessVisit ? 'Patient' : 'Student',
        key: !isSuccessVisit ? 'patient' : 'student',
        component: <Patient />,
        isActiveVisitDependent: false,
        getIsEmpty: (visit) => !visit.patient,
      },
      !isSuccessVisit && {
        label: 'Medical',
        key: 'medical',
        component: <Medical />,
        isActiveVisitDependent: false,
      },
      {
        label: 'Past ' + (!isSuccessVisit ? 'Visits' : 'Sessions'),
        key: !isSuccessVisit ? 'visits' : 'sessions',
        component: <Visits />,
        isActiveVisitDependent: false,
      },
      !isCrisisVisit && {
        label: 'Photos',
        key: 'photos',
        component: <Photos />,
        isActiveVisitDependent: false,
      },
      ...additionalTabs,
      {
        label: !isSuccessVisit ? 'Discharge' : 'Success Plan',
        key: !isSuccessVisit ? 'discharge' : 'successPlan',
        component: <Discharge />,
        isActiveVisitDependent: true,
      },
    ].filter(Boolean),
    sections: [...additionalSections, !isSuccessVisit ? DischargeSummary : SuccessSummary].filter(Boolean),
  }
}

const PrescriptionsTab = {
  label: 'Prescriptions',
  key: 'prescriptions',
  component: <Prescriptions />,
  isActiveVisitDependent: true,
}

const IntakeTab = {
  label: 'Intake',
  key: 'intake',
  component: <Intake />,
  isActiveVisitDependent: true,
}

const DiagnosisTab = {
  label: 'Diagnosis',
  key: 'diagnosis',
  component: <Diagnosis />,
  isActiveVisitDependent: true,
}

const NotesTab = {
  label: 'Notes',
  key: 'notes',
  component: <Notes />,
  isActiveVisitDependent: true,
}

export const SafetyPlanTab = {
  label: 'Safety Plan',
  key: 'safetyPlan',
  component: <SafetyPlan />,
  isActiveVisitDependent: true,
}

const getReferralSection = () => ({
  label: 'patient referral',
  required: true,
  incompleteMessage: '',

  getContent: (visit) =>
    visit.referral.requested ? (
      <ReferralsList visit={visit} />
    ) : (
      <p className="typography-body-l">Referral not requested</p>
    ),

  getIsEmpty: (visit) => {
    if (visit.referral.requested === undefined) return true
    if (!visit.referral.requested) return false
    if (visit.referral.requested) return visit.referral.empty || !visit.referral.correct
  },
})

export const DischargeSummary = {
  title: 'discharge',
  tabKey: 'discharge',
  rows: [
    {
      label: 'care plan',
      required: true,
      incompleteMessage: '',
      getContent: (visit) => <PreLineTypography>{visit.followupInstructions}</PreLineTypography>,
      getIsEmpty: (visit) => !visit.followupInstructions,
    },
    {
      label: 'discharge instructions',
      required: false,
      incompleteMessage: '',
      getContent: (visit) => <DischargeInstructionsList visit={visit} />,
      getIsEmpty: (visit) => !visit.dischargeInstructions.list.length,
      showCompleteIcon: true,
    },
    {
      label: 'medication reconciliation',
      required: (visit) => !!visit.shouldShowMedicationReconciliationSection,
      incompleteMessage: 'Medication reconciliation is incomplete',
      getContent: (visit) => {
        const { reconciled, declineReason, notes } = visit.medicationReconciliation || {}

        return (
          <div className="typography-body">
            <p className="typography-body-l font-medium">{!reconciled ? 'Not' : ''} Reconciled</p>
            {!!declineReason && (
              <p>
                <span className="font-semibold">Reason:</span> {declineReason}
                {!!notes && (
                  <>
                    <br />
                    {notes}
                  </>
                )}
              </p>
            )}
          </div>
        )
      },
      getIsEmpty: (visit) => {
        const { reconciled, declineReason, notes } = visit.medicationReconciliation || {}
        const isReconciledEmpty = typeof reconciled !== 'boolean'

        return isReconciledEmpty || (!reconciled && (!declineReason || (declineReason === 'Other' && !notes)))
      },
      isHidden: (visit) => !visit.shouldShowMedicationReconciliationSection,
    },
    {
      label: 'restrictions',
      required: false,
      incompleteMessage: '',
      getContent: (visit) => <RestrictionsList restrictions={visit.restriction.list} />,
      getIsEmpty: (visit) => !visit.restriction.list.length,
      showCompleteIcon: true,
    },
    {
      label: 'resume activities on',
      required: false,
      incompleteMessage: '',

      getContent: (visit) => formatDate(visit.restriction.resumeDateAdopted),
      getIsEmpty: (visit) => !visit.restriction.resumeDateAdopted,
    },
    {
      label: 'follow up visit',
      required: false,
      incompleteMessage: '',

      getContent: (visit) => format(visit.followupAt, "EEEE, MMMM do 'at' hh:mm aaa"),
      getIsEmpty: (visit) => !visit.followupAt,
    },
    getReferralSection(),
  ],

  getSection: () => null,
}

export const SuccessSummary = {
  title: 'success plan',
  tabKey: 'successPlan',
  rows: [
    {
      label: 'success plan',
      required: true,
      incompleteMessage: '',
      getContent: (visit) => <PreLineTypography>{visit.followupInstructions}</PreLineTypography>,
      getIsEmpty: (visit) => !visit.followupInstructions,
    },
  ],

  getSection: () => null,
}

const DiagnosisSummary = {
  title: 'visit summary',
  tabKey: 'diagnosis',
  rows: [
    {
      label: 'diagnosis',
      required: true,
      incompleteMessage: '',
      getContent: (visit) => <DiseasesList diseases={visit.dxs.list} />,
      getIsEmpty: (visit) => !visit.dxs.list.some((dx) => dx.code || dx.description),
    },
    {
      label: 'history notes',
      required: true,
      incompleteMessage: '',
      getContent: (visit) => <FormattedNote note={visit.notes.history?.description} />,
      getIsEmpty: (visit) => !visit.notes.history?.description,
    },
    {
      label: 'exam notes',
      required: true,
      incompleteMessage: '',
      getContent: (visit) => <FormattedNote note={visit.notes.exam?.description} />,
      getIsEmpty: (visit) => !visit.notes.exam?.description,
    },
  ],
  getSection: () => null,
}

const getNotesSummary = (isSuccess = false) => ({
  title: 'Notes',
  tabKey: 'notes',
  rows: [
    {
      label: `${!isSuccess ? 'visit' : 'session'} notes`,
      isHidden: (visit) => visit.isVisitNoteHidden,
      required: (visit) => visit.isVisitNoteRequired,
      incompleteMessage: '',
      getContent: (visit) => <FormattedNote note={visit.notes.history?.description} />,
      getIsEmpty: (visit) => !visit.notes.history?.description,
    },
    {
      label: 'private notes',
      required: false,
      incompleteMessage: '',
      getContent: (visit) => <FormattedNote note={visit.notes.psychotherapy?.description} />,
      getIsEmpty: (visit) => !visit.notes.psychotherapy?.description,
    },
    {
      label: 'diagnosis',
      required: false,
      incompleteMessage: '',
      getContent: (visit) => <DiseasesList diseases={visit.dxs.list} />,
      getIsEmpty: (visit) => !visit.dxs.list.some((dx) => dx.code || dx.description),
    },
  ],
  getSection: () => null,
})

const SafetyPlanSummary = {
  title: 'safety plan',
  tabKey: 'safetyPlan',
  rows: [
    {
      label: 'safety plan',
      // Required is a BE calculated field, if phq-9 is endorsed: true. If safety plan is created during visit, or edits to existing are made: true, otherwise required will not exist
      required: (visit) => visit?.safetyPlan?.required ?? false,
      incompleteMessage: 'Safety plan question incomplete',
      getContent: () => <PreLineTypography>Safety plan question complete</PreLineTypography>,
      getIsEmpty: (visit) =>
        (!!visit?.safetyPlan?.form && !visit?.safetyPlan?.attestation && visit?.safetyPlan?.required) ||
        (visit?.safetyPlan?.required && !visit?.safetyPlan?.form),
      showCompleteIcon: true,
    },
  ],
  getSection: () => null,
}

const StudentSummary = {
  title: 'student',
  tabKey: 'student',
  rows: [
    {
      label: 'student',
      required: true,
      incompleteMessage: 'Required Student questions incomplete',
      getContent: () => <PreLineTypography>Required Student questions complete</PreLineTypography>,
      getIsEmpty: (visit) => {
        const memberInfo = MemberExtendedData.get(visit.memberId)
        const { degreeClassification, major, studentExperiences, graduationYear, graduationSemester } =
          memberInfo?.privateData?.member ?? {}

        const completed =
          !!degreeClassification &&
          major?.length > 0 &&
          studentExperiences?.length > 0 &&
          !!graduationSemester &&
          !!graduationYear

        return !completed
      },
    },
  ],
  getSection: () => null,
}

const getPrescriptionsSummary = () => ({
  title: 'prescriptions',
  tabKey: 'medical',
  rows: [],
  getSection: (visit) => <PrescriptionsSection visit={visit} />,
})

const IntakeSummary = {
  tabKey: 'intake',
  title: 'intake',
  rows: [
    {
      label: 'intake',
      required: true,
      incompleteMessage: 'Required intake questions incomplete',
      getContent: (visit) =>
        visit.questionnaireVisitType && visit.intakeQuestionnaire.filled ? 'Required intake questions complete' : '',
      getIsEmpty: (visit) => !visit.questionnaireVisitType || !visit.intakeQuestionnaire.filled,
    },
  ],
  getSection: () => null,
}

export const getContentWithFeatureFlags = (flags, visit) => {
  const { visitType } = visit || {}

  const _PrescriptionsSummary = !User.isHidePrescriptionsFeatureFlagEnabled ? getPrescriptionsSummary() : null

  const _SafetyPlanSummary =
    !!visit?.isCrisis && !visit?.safetyPlan?.required && !visit?.safetyPlan?.form ? null : SafetyPlanSummary

  switch (visitType) {
    case VisitTypes.medical:
      return getFinalContent({
        additionalTabs: [DiagnosisTab],
        additionalSections: [DiagnosisSummary, _PrescriptionsSummary],
        visitType,
      })
    case VisitTypes.healthCoach:
      return getFinalContent({ additionalTabs: [NotesTab], additionalSections: [getNotesSummary()], visitType })
    case VisitTypes.therapy:
    case VisitTypes.crisis:
      return getFinalContent({
        additionalTabs: [IntakeTab, SafetyPlanTab, NotesTab],
        additionalSections: [IntakeSummary, _SafetyPlanSummary, getNotesSummary()],
        visitType,
      })
    case VisitTypes.psychiatry:
      return getFinalContent({
        additionalTabs: [IntakeTab, SafetyPlanTab, DiagnosisTab],
        additionalSections: [IntakeSummary, _SafetyPlanSummary, DiagnosisSummary, _PrescriptionsSummary],
        visitType,
      })
    case VisitTypes.success:
      return getFinalContent({
        additionalTabs: [NotesTab],
        additionalSections: [getNotesSummary(true), StudentSummary],
        visitType,
      })
    default:
      return getFinalContent({ visitType })
  }
}
